// angular
import {
    Component,
    OnInit,
    OnChanges,
    ViewChild,
    Input,
    EventEmitter,
    Output,
    SimpleChanges,
    OnDestroy, ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap/modal';
// services
import { AuthService } from '../../../core/auth.service';
import { InteractiveMapService } from './interactive-map.service';
import { BaseService } from '../../../shared/base.service';
// others
import { BaseComponent } from '../../../shared/base.component';
import { AppService } from '../../../app.service';
import { PatientService } from '../../patient.service';
import * as moment from 'moment';
import {PerfectScrollbarComponent} from 'ngx-perfect-scrollbar';
import { AccordionComponent } from 'ngx-bootstrap/accordion';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { TabsetComponent } from 'ngx-bootstrap/tabs';

@Component({
    selector: 'interactive-map-component',
    templateUrl: 'interactive-map.component.html',
    styleUrls: ['interactive-map.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        InteractiveMapService,
        BaseService
    ]
})

export class InteractiveMapComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isViewOnly = false;
    @Input() items: Array<Object>; // Structured diagnosis items, to be used in tree-selector.
    @Input() symptomMapId: number;
    @Input() activeItemId: any;
    @Input() activeItem: any;
    @Input() nodeClassList: any;
    @Input() isCollapsed: boolean;
    @Input() isFullScreen: boolean;
    @Input() isComparisonViewMode: boolean;
    @Input() showLegends: boolean;
    @Input() isDisabled : boolean;

    @Output() updateMap = new EventEmitter();
    @Output() highlightDomain = new EventEmitter();
    @Output() highlightSymptom = new EventEmitter();
    @Output() closeInteractive = new EventEmitter();
    @Output() setMapDisable = new EventEmitter();
    @Output() openProposalDiagnosis = new EventEmitter();
    @Output() showErrorModal = new EventEmitter();

    diagnosisList = [];
    selectionList = {
        mild: {
            name: 'mild',
            rating: 16.66,
            class: 'rating-low'
        },
        moderate: {
            name: 'moderate',
            rating: 50,
            class: 'rating-middle'
        },
        severe: {
            name: 'severe',
            rating: 83.33,
            class: 'rating-high'
        },
        none: {
            name: 'none',
            rating: 1,
            class: 'rating-none'
        },
        anamnestic: {
            name: 'anamnestic',
            rating: 1,
            class: 'anamnestic'
        }
    };

    domainColor = {
        unhighlight : 0,
        highlight : 1,
        grey : 2
    }

    domainRating = {
        mild : 0,
        mildtomiddle : 1,
        middle : 2,
        middletosevere : 3,
        severe : 4
    }

    interactiveMapSub: Subscription;

    @ViewChild('ps', { static: false }) perfectScrollbarComp: PerfectScrollbarComponent;
    @ViewChild('psSymptom', { static: false }) perfectScrollbarSymptomComp: PerfectScrollbarComponent;
    @ViewChild('tabs', {static: false}) tabs: TabsetComponent;
    @ViewChild('accordion', { static: false }) accordionComp: AccordionComponent;

    disabledNoneDiagnosis : boolean = false;
    lang: string = this.translate.currentLang;
    mapData = {
        domain: [],
        symptoms: [],
        markedDomain: 0,
        unmarkedDomain: 0,
        totalDomainOnTaskList : 0,
    };
    domain: any;
    activeDomain: any;
    selectedSymptom: any;
    selectedFeelings: any;
    selectedFeeling: any;
    primaryOnMapItem: any;
    hoverOnSymptom = false;
    targetSymptom: any;
    timer: any;
    countUpdated = 0;
    counting = {
        total: 0,
        todo: 0,
        done: 0
    };
    scrollOutHeader = false;
    scrollOutSymptomHeader = false;
    stickyWidth = '474px';
    tabSelected = 0;
    initMapCompleted = false;
    timing = 0;
    finishedCV = false;

    constructor(
        public authService: AuthService,
        public baseService: BaseService,
        public router: Router,
        public appService: AppService,
        public route: ActivatedRoute,
        public patientService: PatientService,
        public interactiveMapService: InteractiveMapService,
        public translate: TranslateService
    ) {
        super(authService, router, baseService, appService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.translate.onLangChange.subscribe(value => {
            this.lang = value.lang;
        });
        this.setMapDisable.emit(true);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['symptomMapId'] && this.symptomMapId) {
            return this.initInteractiveMap();
        }
        if (changes['isCollapsed']) {
            this.timing = this.isCollapsed ? 0 : Date.now();
        }
        if (changes['activeItemId'] && this.mapData) {
            if (!this.activeItem || !this.activeItemId) {
                this.selectedSymptom = null;
                return;
            }
            /**
             *  Case 1 : Click on Domain
             */
            if(this.activeItem.type === 'domain'){
                let domain;
                domain = _.find(this.mapData.domain, ['domain_map_id', this.activeItem.domainId]);
                if (domain) {
                    return this.setActiveDomain(domain);
                }
            }
            /**
             *  Case 2: Click on Symptom level
             */
            let symptom;
            const systemId = this.activeItem.systemId || 0;
            for (const domain of this.mapData.domain) {
                symptom = _.find(domain.symptoms, ['system_id', Number(systemId)]);
                if (symptom) {
                    return this.setActiveSymptom(symptom, domain);
                }
            }
        }
    }

    ngOnDestroy() {
        if(this.interactiveMapSub) this.interactiveMapSub.unsubscribe();
    }

    initInteractiveMap(){
        this.isLoading = true;
        this.interactiveMapService.getData(this.symptomMapId).subscribe(
            data => {
                this.isLoading = false;
                if(data['result']){
                    this.setMapDisable.emit(false);
                    return this.generateInteractiveData(data['result']);
                }
                const wrapper = document.getElementById('interactive-map');
                this.stickyWidth = (wrapper.offsetWidth - 6) + 'px';
            }
        );
    }

    generateInteractiveData(data){
        this.mapData = {
            domain: [],
            symptoms: [],
            markedDomain: 0,
            unmarkedDomain: 0,
            totalDomainOnTaskList : 0,
        };
        this.counting = {
            total: 0,
            todo: 0,
            done: 0
        };
        data.forEach(domain => {
                this.mapData.domain.push(domain);
                domain.is_marked && domain.is_marked === 1 && domain.is_show_on_task_list && domain.is_show_on_task_list === 1 ? this.mapData.markedDomain++ : this.mapData.unmarkedDomain++;
                if (domain.is_show_on_task_list && domain.is_show_on_task_list === 1) {
                    this.mapData.totalDomainOnTaskList++;
                }
                for (let index in domain.symptoms) {
                    const symptom = domain.symptoms[index];
                    symptom.have_statement = !symptom.feelings.some((item) => {
                        return item['statement_' + this.lang] === '';
                    });
                    this.mapData.symptoms.push(symptom);
                }

                // Counting Done
                if (!domain.is_show_on_task_list) {
                    return;
                }
                this.counting.todo += domain.total_expert_items - domain.symptom_marked_count ;
                this.counting.done += domain.symptom_marked_count + domain.is_marked;
                this.counting.total += domain.total_expert_items + domain.is_show_on_task_list;
                this.countUpdated =  this.counting.done;
            });
        this.initMapCompleted = true;
    }

    updateInteractiveDomainData(domain, params, domainUpdated) {
        if ('is_marked' in params) {
            if (params.is_marked === 1) {
                this.mapData.markedDomain++;
                this.mapData.unmarkedDomain--;
            } else {
                this.mapData.markedDomain--;
                this.mapData.unmarkedDomain++;
            }
        }

        const _self = this;
        setTimeout(function(){
            for (const [key, value] of Object.entries(params)) {
                domain[key] = value;
            }
            const domainIndex = _self.mapData.domain.indexOf(domain);
            const oldDomainData = _self.mapData.domain[domainIndex];
            const mergedObject = _.merge(oldDomainData, domainUpdated, (objValue, srcValue) => {
                if (_.isObject(objValue)) {
                    return _.merge(objValue, srcValue);
                }
            });
            _self.mapData.domain[domainIndex] = mergedObject;
            // Counting again for Tasklist
            _self.countingTasklist();
        }, 800);
    }

    countingTasklist(){
        this.counting = {
            total: 0,
            todo: 0,
            done: 0
        };
        this.mapData.totalDomainOnTaskList = 0;
        this.mapData.domain.forEach(domain => {
            // Counting Done
            if (!domain.is_show_on_task_list) {
                return;
            }
            this.counting.todo += domain.total_expert_items - domain.symptom_marked_count ;
            this.counting.done += domain.symptom_marked_count + domain.is_marked;
            this.counting.total += domain.total_expert_items + domain.is_show_on_task_list;
            this.mapData.totalDomainOnTaskList++;
        });
        const _self = this;
        _self.finishedCV = _self.counting.done === _self.counting.total ? true : false;
        setTimeout(function () {
            _self.countUpdated = _self.counting.done;
        }, 1000);
    }

    setActiveSymptom(symptom, domain = {}){
        if (!symptom || !domain) {
            this.selectedSymptom = null;
            this.highlightSymptom.emit(false);
            return false;
        }
        this.selectedSymptom = symptom;
        this.domain = domain;
        this.selectedFeelings = symptom['feelings'];
        this.selectedFeeling = symptom['feelings'][0];
        this.primaryOnMapItem = this.selectedFeelings.find(function (feeling) {
            return feeling.is_primary_on_map === 1;
        });
        // Reset scrollbar
        const _self = this;
        this.scrollOutSymptomHeader = false;
        setTimeout(function () {
            _self.perfectScrollbarSymptomComp.directiveRef.update();
            _self.perfectScrollbarSymptomComp.directiveRef.scrollToTop(0);
        }, 1000);
    }

    setHighlightSymptom($event, symptom){
        if(this.hoverOnSymptom) {
            return false;
        }
        // Return if hover on child element of the same item
        if (this.targetSymptom && this.targetSymptom.innerText === $event.currentTarget.innerText) {
            return;
        }
        const _self = this;
        this.targetSymptom = $event.currentTarget || $event.srcElement;
        this.hoverOnSymptom = true;
        this.timer = setTimeout(function () {
            _self.highlightSymptom.emit(symptom);
            _self.hoverOnSymptom = false;
        }, 300);
    }
    unhighLightSymptom($event, symptom){
        const elementRelated = $event.toElement || $event.relatedTarget;
        if (this.targetSymptom.contains(elementRelated) || ($event.toElement && this.targetSymptom.innerText === $event.toElement.innerText) || this.selectedSymptom) {
            return;
        }
        clearTimeout(this.timer);
        this.hoverOnSymptom = false;
        this.targetSymptom = null;
        this.highlightSymptom.emit(false);
    }

    setActiveDomain(domain){
        // Clear selected symptom and domain active before
        this.selectedSymptom = null;
        // Set active Domain
        this.activeDomain = domain || null;
        if (this.perfectScrollbarComp) {
            this.perfectScrollbarComp.directiveRef.update();
            this.perfectScrollbarComp.directiveRef.scrollToTop(0);
            this.perfectScrollbarComp.directiveRef.update();
        }
        this.scrollOutHeader = false;
        return this.highlightDomain.emit(domain);
    }

    updateRatingTaskList(domain, symptom, feelingIndex, ratingPoint, isAnamnestic = 0){
        const feeling = symptom['feelings'][feelingIndex];
        // // Move to Done tab if don't have sub symptoms
        symptom['passed'] = symptom['feelings'].length === 1 ? true : false;
        return this.updateRating(feeling, ratingPoint, symptom, isAnamnestic, symptom.domain_id, domain);
    }

    updateRating(feeling, ratingPoint, symptom, isAnamnestic = 0, domainId = 0, domain = null) {
        const newRating = this.selectionList[ratingPoint]['class'];
        if (feeling.rating === newRating && this.activeDomain)  {
            return;
        }
        feeling.rating = newRating;
        if (!this.symptomMapId) {
            return;
        }
        this.setMapDisable.emit(true);
        const feelingId = feeling.id;
        feeling.rating = this.selectionList[ratingPoint]['class'];
        let primary = feeling.is_primary_on_map;

        // Auto Set Primary for Symptom have multi levels
        if (symptom) {
            const primaryItem = symptom['feelings'].find(function(item) {
                return item.is_primary_on_map === 1;
            });
            // If don't have any primary item => set this feeling to primary
            if(!primaryItem) {
                primary = 1;
            }
        }
        feeling.is_primary_on_map = primary;
        const data = {
            patient_questionnaire_id: this.symptomMapId,
            domain_id: domainId && !this.selectedSymptom ? domainId : this.selectedSymptom.domain_id,
            feelings: [feelingId],
            selected_feeling: isAnamnestic ? feeling.anamnestic_feeling_id : feelingId,
            anam_feelings: feeling.anamnestic_feeling_id ? [feeling.anamnestic_feeling_id] : [],
            selected: ratingPoint !== 'none' ? 1 : 0,
            rating: this.selectionList[ratingPoint]['rating'],
            is_primary  : primary,
            lang : this.lang,
            isAcute: isAnamnestic ? 0 : 1
        };

        this.interactiveMapService.updateRating(data).subscribe((rs) => {
            if (rs['results'] && rs['success']) {
                const resDomain = rs['results']['domain'];
                const res = {
                    data: resDomain['data'],
                    rating: ratingPoint,
                    icon: rs['results']['icon'],
                    item: data,
                    domainId: resDomain['data']['domain_map_id'],
                    domainCls: resDomain['domain_class'],
                    isAnamnestic: isAnamnestic,
                    activeDomain: null,
                    activeSymptom: this.selectedSymptom
                };
                // If this feeling is Primary => Update on map
                if(this.primaryOnMapItem && feelingId === this.primaryOnMapItem.id){
                    this.selectedFeeling = feeling;
                }
                /**
                 * Only re-bind data for domain in 2 case :
                 * 1. Rating in Details mode
                 * 2. Rating in Tasklist with Symptom have no sub
                 */
                const currentDomain = domain || this.domain;
                if (this.selectedSymptom || symptom['feelings'].length === 1) {
                    this.updateInteractiveDomainData(currentDomain, {}, resDomain['data']);
                }

                //Update current active domain
                if (this.activeDomain) {
                    //On domain mode => only update highlight tags
                    this.activeDomain.is_highlight = resDomain['data']['is_highlight'];
                    //On detail mode => update all active domain
                    if(this.selectedSymptom) {
                        this.activeDomain = resDomain['data'];
                    }
                    res.activeDomain = this.activeDomain;
                }
                //Clear Selected Diagnosis to get new data
                this.patientService.proposalData = [];
                this.appService.publish('patient', { selectedDiagnosesAreUpdated: true });

                this.setMapDisable.emit(false);
                return this.updateMap.emit(res);
            }
        },
        (error) => {
            symptom['passed'] = false;
            this.setMapDisable.emit(false);
            this.showError(error.error.error);
        });
    }

    updatePrimary(feeling, symptom = null) {
        if ((!this.symptomMapId || !this.activeItem) && !symptom) {
            return;
        }

        const selectedFeelings = symptom ? symptom['feelings'] : this.selectedFeelings;
        const feelingIds = selectedFeelings.map(function(item){
            return item.id;
        });
        const anamnesticFeelingIds = selectedFeelings.flatMap(function(item) {
            return item.anamnestic_feeling_id ? item.anamnestic_feeling_id : [];
        });

        // Update primary item
        feeling.is_primary_on_map = 1;
        this.selectedFeeling = feeling;
        this.primaryOnMapItem = feeling;
        selectedFeelings.forEach(function(item, index) {
            item.is_primary_on_map = item.id === feeling.id ? 1 : 0;
        });

        const data = {
            patient_questionnaire_id: this.symptomMapId,
            feelings: feelingIds.concat(anamnesticFeelingIds),
            primary_id: feeling.anamnestic_feeling_id ? [feeling.id].concat(feeling.anamnestic_feeling_id) : [feeling.id],
            lang : this.lang,
            domain_id : symptom ? symptom.domain_id : this.selectedSymptom.domain_id,
        };
        this.interactiveMapService.updatePrimary(data).subscribe((rs) => {
            if (rs['results'] && rs['success']) {
                const resDomain = rs['results']['domain'];
                const res = {
                    data: rs['results']['data'],
                    icon: rs['results']['icon'],
                    system_id: rs['results']['system_id'],
                    domainCls: resDomain['domain_class'],
                    domainId: resDomain['data']['domain_map_id'],
                    feeling: feeling,
                    activeSymptom: this.selectedSymptom,
                    activeDomain: this.activeDomain,
                };
                return this.updateMap.emit(res);
            }
        },
        (error) => {
            this.showError(error.error.error)
        });
    }

    closeDetails(isViewOnly) {
        this.selectedSymptom = null;
        if (!isViewOnly) {
            return this.closeInteractive.emit({
                interactive: true,
                fullscreen: true,
                selectedSymptom: this.selectedSymptom
            });
        }
        this.setActiveDomain(false);
        return this.closeInteractive.emit(false);
    }

    goBack(updateDomainData = false) {
        if(updateDomainData) {
            // Update data for Domain after rating Domain Level
            this.mapData.domain.forEach(domain => {
                if (domain.id === this.activeDomain.id) {
                    domain.symptoms = this.activeDomain.symptoms;
                    return;
                }
            });
        }
        this.setActiveSymptom(false);
        this.setActiveDomain(false);

        // Need time to render tabs
        setTimeout(() => {
            this.selectTab(this.tabSelected);
        }, 200);
    }

    updateDomain(domain, params){
        if (this.isLoading) {
            return;
        }
        let data = {
            patientQuestionnaireId : this.symptomMapId,
            domainId: domain.id,
            params: params,
            // excludeSymtoms: 1
        };
        this.isLoading = true;
        this.interactiveMapService.updateDomain(data).subscribe((rs) => {
            if (rs && rs['success']) {
                const resDomain = rs['results']['domain'];
                this.isLoading = false;
                domain['passed']= resDomain['data']['is_marked'];
                domain['rejected'] = !domain['passed'];
                this.updateInteractiveDomainData(domain, params, resDomain['data']);
                const res = {
                    data: resDomain['data'],
                    domainId: resDomain['data']['domain_map_id'],
                    domainCls: resDomain['domain_class'],
                    activeDomain: this.activeDomain,
                };
                this.patientService.proposalData = rs['proposal_diagnosis'];
                return this.updateMap.emit(res);
            }
        },
        (error) => {
           this.showError(error.error.error);
        });
    }

    updateSymptom(domain, symptom, params) {
        let data = {
            patientQuestionnaireId : this.symptomMapId,
            domainId: symptom.domain_id,
            symptomId: symptom.id
        };
        symptom['passed'] = true;
        this.interactiveMapService.updateSymptomDone(data).subscribe((rs) => {
            if (rs && rs['success']) {
                const _self = this;
                symptom.is_marked = 1;
                domain.symptom_marked_count++;
                this.counting.done++;
                _self.finishedCV = _self.counting.done === _self.counting.total ? true : false;
                setTimeout(function () {
                    _self.countUpdated = _self.counting.done;
                }, 1000);
            }
        });
    }

    totalRatedSubsymptoms(symptom){
        if(!symptom){
            return 0;
        }
        const countfiltered = symptom.feelings.filter(function(feeling){
            return feeling.rating && feeling.rating !== '';
        }).length;
        return countfiltered;
    }

    onScrollTasklist($event, symptomLevel = false) {
        if ($event.srcElement.scrollTop < 180) {
            if (symptomLevel) {
                this.scrollOutSymptomHeader = false
            } else {
                this.scrollOutHeader = false
            }
            return;
        }
        if (symptomLevel) {
            this.scrollOutSymptomHeader = true
        } else {
            this.scrollOutHeader = true
        }
    }

    selectTab(tabId: number) {
        if (this.tabs && this.tabs.tabs.length > 0) {
            this.tabs.tabs[tabId].active = true;
            this.tabSelected = tabId;
        }
    }

    navigateToPatients(){
        const _self = this;
        _self.closeInteractive.emit(false);
        setTimeout(function () {
            return _self.router.navigate(['patients']);
        }, 1000);
    }

    showError(message) {
        this.showErrorModal.emit({ 
            params: { 
                icon: 'fas fa-exclamation-triangle', 
                warning: true 
            },
            errorMessage : message
        });
    }
}
